import type { EventParticipationSummary, NotificationDeliveryCheckResponse, NotificationSendPayload } from "../../../api/services/notification-center.service";

export const NOTIFICATION_BATCH_SIZE = 3000;

export function splitRecipients(ids: number[]) {
  const unique = [...new Set(ids)];
  const batches: number[][] = [];
  for (let index = 0; index < unique.length; index += NOTIFICATION_BATCH_SIZE) {
    batches.push(unique.slice(index, index + NOTIFICATION_BATCH_SIZE));
  }
  return batches;
}

export type CheckedNotificationBatch = {
  selection: Pick<NotificationSendPayload, "recipient_ids" | "channels" | "recipient_channel_overrides">;
  result: NotificationDeliveryCheckResponse;
};

export function combineDeliveryChecks(batches: CheckedNotificationBatch[]): NotificationDeliveryCheckResponse {
  const results = batches.map((batch) => batch.result);
  const first = results[0];
  const amountInCents = results.reduce((total, result) => total + Math.round(Number(result.max_estimated_charge ?? result.alimtalk_cost?.estimated_amount ?? 0) * 100), 0);
  const balances = results.map((result) => result.wallet?.available_balance).filter((value) => value != null).map((value) => Math.round(Number(value) * 100));
  const enoughTotalCredit = !results.some((result) => result.credit_enforced)
    || (balances.length > 0 && Math.min(...balances) >= amountInCents);
  return {
    ...first,
    // A summary is never an authorization token. Each batch retains its own.
    preflight_token: undefined,
    recipients: results.flatMap((result) => result.recipients || []),
    server_preview: results.find((result) => result.server_preview?.content)?.server_preview,
    preview_error: results.find((result) => result.preview_error)?.preview_error,
    max_estimated_charge: amountInCents / 100,
    credit_sufficient: enoughTotalCredit && results.every((result) => result.credit_sufficient !== false),
  };
}

export type BatchSendProgress = { completed: number; total: number; created: number; event_participation?: EventParticipationSummary };
export class NotificationBatchSendError extends Error {
  constructor(
    public readonly cause: unknown,
    public readonly progress: BatchSendProgress,
    public readonly remainingIds: number[],
    public readonly uncertainIds: number[],
  ) {
    super("분할 발송이 중단되었습니다.");
  }
}

export async function sendNotificationBatches({ batches, content, check, send, onProgress, shouldContinue = () => true }: {
  batches: CheckedNotificationBatch[];
  content: Omit<NotificationSendPayload, "recipient_ids" | "channels" | "recipient_channel_overrides" | "preflight_token">;
  check: (payload: NotificationSendPayload) => Promise<{ data: NotificationDeliveryCheckResponse }>;
  send: (payload: NotificationSendPayload) => Promise<{ data: { created: number; event_participation?: EventParticipationSummary } }>;
  onProgress: (progress: BatchSendProgress) => void;
  shouldContinue?: () => boolean;
}) {
  const sendable = batches.filter((batch) => batch.selection.channels.length > 0);
  const progress: BatchSendProgress = { completed: 0, total: sendable.length, created: 0 };
  onProgress({ ...progress });
  for (let index = 0; index < sendable.length; index += 1) {
    const batch = sendable[index];
    let attempted = false;
    try {
      if (!shouldContinue()) throw new Error("발송 화면을 벗어나 후속 발송을 중단했습니다.");
      const payload = { ...content, ...batch.selection };
      // A long run can outlive the original 15-minute tokens. Only an unstarted
      // batch gets a new token; never repeat an uncertain send automatically.
      const { data } = await check(payload);
      if (!shouldContinue()) throw new Error("발송 화면을 벗어나 후속 발송을 중단했습니다.");
      if (!data.preflight_token || data.credit_sufficient === false) {
        throw new Error(data.preview_error || (data.credit_sufficient === false ? "알림톡 충전금이 부족합니다." : "수신 가능 여부가 변경되었습니다. 다시 확인해 주세요."));
      }
      attempted = true;
      const response = await send({ ...payload, preflight_token: data.preflight_token });
      if (!Number.isInteger(response.data.created) || response.data.created < 0 || response.data.created > payload.recipient_ids.length) {
        throw new Error("발송 접수 응답을 확인할 수 없습니다. 통합 내역을 확인해 주세요.");
      }
      progress.completed += 1;
      progress.created += response.data.created;
      if (response.data.event_participation) {
        const registration = response.data.event_participation;
        progress.event_participation = {
          event_name: registration.event_name,
          new_count: (progress.event_participation?.new_count || 0) + registration.new_count,
          existing_count: (progress.event_participation?.existing_count || 0) + registration.existing_count,
        };
      }
      onProgress({ ...progress });
    } catch (cause) {
      throw new NotificationBatchSendError(
        cause, { ...progress },
        sendable.slice(index + (attempted ? 1 : 0)).flatMap((item) => item.selection.recipient_ids),
        attempted ? batch.selection.recipient_ids : [],
      );
    }
  }
  return progress;
}
